import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCrewAvatarUrls, type CrewAvatarUrlMap } from "@/lib/crewAvatars";

/**
 * Resolve every crew member's profile-photo URL for a briefing, keyed by their
 * auth user_id. Cached per briefing so the many avatar call sites on a page
 * (crew list, schedule, live tracking) share one fetch + one batch of signed
 * URLs instead of each resolving independently.
 *
 * `urlFor(userId)` returns the signed URL for a crew member, or undefined when
 * they have no photo (the <CrewAvatar> then falls back to initials).
 */
export function useCrewAvatars(briefingId: string | null | undefined): {
  urlFor: (userId: string | null | undefined) => string | undefined;
  /** Raw user_id → URL map (stable reference from React Query). Depend on this
   *  in effects that build markers imperatively so they re-run when photos load. */
  avatars: CrewAvatarUrlMap | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery<CrewAvatarUrlMap>({
    queryKey: ["crew-avatars", briefingId],
    queryFn: () => fetchCrewAvatarUrls(briefingId as string),
    enabled: !!briefingId,
    // Signed URLs live 1h; refetch comfortably inside that window so a long
    // session doesn't render dead links.
    staleTime: 30 * 60 * 1000,
  });

  // Memoised on the query data: the map-building effects that draw crew
  // markers list urlFor in their dependency array, and a fresh arrow on
  // every render made them tear down and rebuild every marker — on a page
  // that re-renders on each GPS fix, that was several times a second.
  const urlFor = useCallback(
    (userId: string | null | undefined) => (userId && data ? data.get(userId) : undefined),
    [data],
  );

  return { urlFor, avatars: data, isLoading };
}
